"use client";

import { useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { useAuth } from "@/hooks/useAuth";
import { usePaperTrading } from "@/hooks/usePaperTrading";
import { useCftcGate } from "@/hooks/useCftcGate";
import { useTenant } from "@/hooks/useTenant";
import { useTradeStore } from "@/stores/tradeStore";
import { OrderEntryPanel } from "./OrderEntryPanel";
import { PositionList, type Position } from "./Positions";
import { HistoryTabs, type Order, type Trade } from "./History";
import { FirstTradeDisclosureModal } from "@/components/trade/FirstTradeDisclosureModal";
import { isTenantGateReady } from "@/components/tenant/TenantContextProvider";
import type { SupportedAsset, OrderFormData } from "@/stores/paperTradingStore";

interface PaperTradingPanelProps {
  currentPrice: number;
  className?: string;
}

export const PaperTradingPanel = ({
  currentPrice,
  className,
}: PaperTradingPanelProps) => {
  const { session } = useAuth();
  const isConnected = !!session?.user;
  const { market } = useTradeStore();
  // SFWL-017: shared CFTC §1.55(b) acknowledgement gate (also used by
  // AllocationModal and ApplyConfirmationModal). Migrated from this
  // component's original SFWL-007 inline wiring — reduce-only orders still
  // bypass the gate entirely (see handleSubmitOrder), which useCftcGate
  // itself has no opinion on.
  const { runGated, modalProps } = useCftcGate();
  // SFWL-017 Codex P1 fix: fail closed on order submit while tenant
  // resolution is "pending" or "failed" — see AllocationModal.tsx for the
  // full rationale. Reduce-only orders still bypass the gate entirely (see
  // handleSubmitOrder), consistent with the existing CFTC-gate carve-out.
  const { resolution } = useTenant();
  const tenantGateReady = isTenantGateReady(resolution);
  // The gated action below reads this at ACKNOWLEDGE time, not at
  // submit-click time — a user can sit on the disclosure for a while, and
  // the order must fill at the price shown when they actually acknowledge,
  // not the stale price from when they clicked submit. Plain assignment in
  // the render body (no effect) keeps it current on every render.
  const currentPriceRef = useRef(currentPrice);
  currentPriceRef.current = currentPrice;

  const {
    account,
    orders,
    trades,
    openPositions,
    openOrders,
    currentPrices,
    activeTab,
    isLoadingAccount,
    isLoadingPositions,
    isLoadingOrders,
    isLoadingTrades,
    isSubmittingOrder,
    error,
    submitOrder,
    cancelOrder,
    closePosition,
    setActiveTab,
    updatePrice,
    clearError,
    resetAccount,
  } = usePaperTrading();

  // Update current price in store when it changes
  useEffect(() => {
    if (currentPrice > 0) {
      updatePrice(market as SupportedAsset, currentPrice);
    }
  }, [currentPrice, market, updatePrice]);

  // Build prices map for positions
  const pricesMap = useMemo(() => {
    const map: Record<string, number> = {};
    Object.entries(currentPrices).forEach(([asset, price]) => {
      map[asset] = price || currentPrice;
    });
    // Ensure current market has current price
    map[market] = currentPrice;
    return map;
  }, [currentPrices, currentPrice, market]);

  // Calculate unrealized PnL for positions
  const positionsWithPnl: Position[] = useMemo(() => {
    return openPositions.map((p) => {
      const price = pricesMap[p.asset] || p.entryPrice;
      const entryPrice = p.entryPrice;
      const size = p.size;

      let unrealizedPnl: number;
      if (p.side === "long") {
        unrealizedPnl = (price - entryPrice) * (size / entryPrice);
      } else {
        unrealizedPnl = (entryPrice - price) * (size / entryPrice);
      }

      const unrealizedPnlPercent = p.margin > 0 ? (unrealizedPnl / p.margin) * 100 : 0;

      return {
        ...p,
        unrealizedPnl,
        unrealizedPnlPercent,
      };
    });
  }, [openPositions, pricesMap]);

  const handleSubmitOrder = async (formData: OrderFormData) => {
    // Reduce-only orders are closing/reducing exposure and pass straight
    // through per locked OQ4 — bypass the CFTC gate entirely, regardless of
    // tenant scope or ack state. Everything else (opening orders) routes
    // through the shared gate: futures tenants without a persisted ack get
    // stashed behind the disclosure; non-futures tenants and already-acked
    // tenant+user pairs run immediately — zero behavior change for the
    // default Sovrun experience.
    if (formData.reduceOnly) {
      await submitOrder(formData, currentPrice);
      return;
    }
    runGated(async () => {
      // Read fresh at ack time (see currentPriceRef above), not the
      // `currentPrice` closed over when this order was first submitted.
      await submitOrder(formData, currentPriceRef.current);
    });
  };

  const handleClosePosition = async (positionId: string) => {
    await closePosition(positionId, currentPrice);
  };

  return (
    <div className={clsx("space-y-4", className)}>
      <FirstTradeDisclosureModal {...modalProps} />

      {/* Error Banner */}
      {error && (
        <div className="flex items-center justify-between rounded-lg bg-rose-500/20 px-4 py-2 text-sm text-rose-400">
          <span>{error}</span>
          <button onClick={clearError} className="text-rose-300 hover:text-white">
            ×
          </button>
        </div>
      )}

      {/* Account Summary */}
      {!isConnected ? (
        <div className="rounded-xl border border-slate-200/60 bg-white/5 p-4">
          <h3 className="text-sm font-semibold text-white">Paper Trading Account</h3>
          <p className="mt-2 text-sm text-slate-400">
            Log in to place paper trades and track positions.
          </p>
        </div>
      ) : (
        account && (
          <div className="rounded-xl border border-slate-200/60 bg-white/5 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Paper Trading Account</h3>
              <button
                onClick={resetAccount}
                className="rounded bg-slate-700 px-2 py-1 text-xs text-slate-300 transition hover:bg-slate-600 hover:text-white"
              >
                Reset
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-slate-500">Balance</p>
                <p className="text-lg font-semibold text-white">
                  $
                  {account.balance.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Available</p>
                <p className="text-lg font-semibold text-emerald-400">
                  $
                  {account.availableBalance.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Margin Used</p>
                <p className="text-lg font-semibold text-amber-400">
                  $
                  {account.marginUsed.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          </div>
        )
      )}

      {/* Order Entry — only when wallet connected */}
      {isConnected && (
        <>
          <OrderEntryPanel
            currentPrice={currentPrice}
            accountBalance={account?.balance ?? 500000}
            marginUsed={account?.marginUsed ?? 0}
            onSubmitOrder={handleSubmitOrder}
            disabled={isSubmittingOrder || isLoadingAccount || !tenantGateReady}
          />

          {/* Tab Navigation */}
          <div className="flex gap-1 rounded-lg bg-slate-800/50 p-1">
            {(["positions", "orders", "trades"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={clsx(
                  "relative flex-1 rounded-md px-3 py-2 text-xs font-medium capitalize transition",
                  activeTab === tab
                    ? "bg-slate-700 text-white"
                    : "text-slate-400 hover:text-white"
                )}
              >
                {tab}
                {tab === "positions" && openPositions.length > 0 && (
                  <span className="ml-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] text-white">
                    {openPositions.length}
                  </span>
                )}
                {tab === "orders" && openOrders.length > 0 && (
                  <span className="ml-1 rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] text-white">
                    {openOrders.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === "positions" && (
              <PositionList
                positions={positionsWithPnl}
                currentPrices={pricesMap}
                onClosePosition={handleClosePosition}
                isLoading={isLoadingPositions}
              />
            )}

            {activeTab === "orders" && (
              <HistoryTabs
                orders={orders as Order[]}
                trades={trades as Trade[]}
                onCancelOrder={cancelOrder}
                isLoading={isLoadingOrders || isLoadingTrades}
              />
            )}

            {activeTab === "trades" && (
              <HistoryTabs
                orders={orders as Order[]}
                trades={trades as Trade[]}
                onCancelOrder={cancelOrder}
                isLoading={isLoadingTrades}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
